import type { IconName } from "@inflow/icons"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"

import Icon from "@/globalComponents/atoms/Icon"

import styles from "./index.module.css"

type Props = {
  message: string
  variant: "success" | "warning" | "error"
  durationMs?: number
  autoDismissable?: boolean
} & (
  | { cancellable: true; onDismiss: () => void }
  | { cancellable?: false; onDismiss?: never }
)

const iconMap: Record<"success" | "warning" | "error", IconName> = {
  success: "check",
  warning: "alert",
  error: "alert",
}

export default function StatusToast({
  message,
  variant,
  durationMs = 10000,
  autoDismissable = true,
  cancellable = false,
  onDismiss,
}: Props) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!autoDismissable) return

    const timer = setTimeout(() => setVisible(false), durationMs)
    return () => clearTimeout(timer)
  }, [durationMs, autoDismissable])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={styles.toast}
          data-variant={variant}
          role={variant === "error" ? "alert" : "status"}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ ease: "easeIn", duration: 0.15 }}
        >
          <Icon name={iconMap[variant]} slot="left" />
          <span>{message}</span>
          {cancellable && (
            <button
              className={styles.dismissButton}
              onClick={() => {
                setVisible(false)
                onDismiss?.()
              }}
              aria-label="Dismiss"
            >
              <Icon name="close" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
